import * as amplitude from '@amplitude/analytics-browser';
import { initAmplitude } from './amplitudeInit';
import { isQaHarnessSession } from './testAccounts';

/**
 * Canonical Amplitude event names (product taxonomy).
 * Use these keys with `eventTracking` / `trackProductEvent`.
 */
export const events = {
  // --- Product taxonomy (tracking spec) ---
  organizationCreated: 'Organization Created',
  programInDesign: 'Program In Design',
  /** One row per finished course creation: fired on the "Create" click that ends
   *  the creation wizard, carrying the FINAL Active Time on Creation. */
  programCreated: 'Program Created',
  programPublished: 'Program Published',
  programInvited: 'Program Invited',
  /** When a program moves to the Active column */
  programActive: 'Program Active',
  userInvited: 'User Invited',
  userAccepted: 'User Accepted',
  userOnboarded: 'User Onboarded',
  lessonCompleted: 'Lesson Completed',
  allLessonsCompleted: 'All Lessons Completed',
  userMatched: 'User Matched',
  messageSent: 'Message Sent',
  meetingScheduled: 'Meeting Scheduled',
  meetingCompleted: 'Meeting Completed',
  objectiveCompleted: 'Objective Completed',
  allObjectivesCompleted: 'All Objectives Completed',
  copilotIntroCompleted: 'Copilot Intro Completed',
  copilotQueried: 'Copilot Queried',
  copilotRetrieved: 'Copilot Retrieved',
  thumbRatedUp: 'Thumb Rated Up',
  thumbsRatedDown: 'Thumbs Rated Down',

  // --- Legacy / additional events (same init pipeline) ---
  /** @deprecated Prefer `events.programActive` — same Amplitude event string */
  programActivated: 'Program Active',
  aimdDocumentsUploaded: 'AIMD Documents Uploaded',
  aimdGeneralPurposeCompleted: 'AIMD General Purpose Completed',
  aimdLearningObjectivesCompleted: 'AIMD Learning Objectives Completed',
  aimdStructureReviewed: 'AIMD Structure Reviewed',
  articleCreated: 'Content Article Created',
  attributesCreated: 'Attributes Created',
  /** @deprecated Prefer `events.copilotQueried` */
  copilotPromptSent: 'Copilot Queried',
  /** @deprecated Prefer `events.copilotIntroCompleted` */
  copilotWelcomeFlowCompleted: 'Copilot Intro Completed',
  defaultMentorCreated: 'Default Mentor Created',
  expertiseAdded: 'Expertise Added',
  /** Learning Gaps — every recommendation on a gap episode marked done or ignored */
  knowledgeGapClosed: 'Knowledge Gap Closed',
  levelCompleted: 'Level Completed',
  materialManuallyCreated: 'Material Manually Created',
  matchByAdmin: 'Match Created (Admin)',
  matchByRequest: 'Match Created (Request)',
  matchCancelled: 'Match Cancelled',
  matchCreated: 'Match Created',
  matchRequested: 'Match Requested',
  meetingCancelled: 'Meeting Cancelled',
  meetingJoinedSuccessfully: 'Meeting Joined Successfully',
  meetingNotJoined: 'Meeting Not Joined',
  meetingRated: 'Meeting Rated',
  meetingReviewed: 'Meeting Reviewed',
  memberDeactivated: 'Member Deactivated',
  mcqAnsweredCorrectly: 'MCQ Answered Correctly',
  mcqAnsweredIncorrectly: 'MCQ Answered Incorrectly',
  msObjectivesSettingsCompleted: 'MS Objectives + Settings Completed',
  msProgramNameAdded: 'MS Program Name Added',
  onboardingFieldsUpdated: 'Onboarding Fields Updated',
  profileUpdated: 'Profile Updated',
  profileViewed: 'Profile Viewed',
  programMemberLabelsAdded: 'Program Member Labels Added',
  programReattempted: 'Program Reattempted',
  sourceAdded: 'Source Added',
  templateSelected: 'Template Selected',
  userAdopted: 'User Adopted',
  userEngaged: 'User Engaged'
} as const;

export type AmplitudeEventKey = keyof typeof events;
export type AmplitudeEventName = (typeof events)[AmplitudeEventKey];

/** Human-readable descriptions for dashboards / docs */
export const eventDescriptions: Record<
  | 'Organization Created'
  | 'Program In Design'
  | 'Program Created'
  | 'Program Published'
  | 'Program Invited'
  | 'Program Active'
  | 'User Invited'
  | 'User Accepted'
  | 'User Onboarded'
  | 'Lesson Completed'
  | 'All Lessons Completed'
  | 'User Matched'
  | 'Message Sent'
  | 'Meeting Scheduled'
  | 'Meeting Completed'
  | 'Objective Completed'
  | 'All Objectives Completed'
  | 'Copilot Intro Completed'
  | 'Copilot Queried'
  | 'Copilot Retrieved'
  | 'Thumb Rated Up'
  | 'Thumbs Rated Down',
  string
> = {
  'Organization Created': 'When an organization is created',
  'Program In Design': 'When a program appears in the In Design column',
  'Program Created':
    'When an admin hits Create on the last step of the creation wizard — carries the final Active Time on Creation',
  'Program Published': 'When a program moves to the Published column',
  'Program Invited': 'When a program moves to the Invited column',
  'Program Active': 'When a program moves to the Active column',
  'User Invited': 'When a user is invited to a program',
  'User Accepted': 'When a user accepts their invitation',
  'User Onboarded': 'When a user completes onboarding',
  'Lesson Completed': 'When a user completes a lesson',
  'All Lessons Completed': 'When a user completes all lessons in a program',
  'User Matched': 'When a user is matched',
  'Message Sent': 'When a user sends a message',
  'Meeting Scheduled': 'When a user schedules a meeting',
  'Meeting Completed': 'When a user completes a meeting',
  'Objective Completed': 'When a user completes an objective',
  'All Objectives Completed': 'When a user completes all objectives',
  'Copilot Intro Completed': 'When a user completes the Copilot welcome flow',
  'Copilot Queried': 'When a user sends a message to Copilot',
  'Copilot Retrieved': 'When Copilot retrieves content from the Vector DB',
  'Thumb Rated Up': 'When a user rates a Copilot response thumbs up',
  'Thumbs Rated Down': 'When a user rates a Copilot response thumbs down'
};

/**
 * Property keys as they should appear on Amplitude events (display names).
 */
export const attributeKeys = {
  adminProgramStatus: 'Admin Program Status',
  // ATC — active admin time spent building a program (BE-accumulated via the
  // AIMD heartbeat). Frozen at the "Create" click that ends the creation wizard
  // and sent on `Program Created`; the program lifecycle events re-send that same
  // frozen value. Programs created before the freeze shipped fall back to the
  // still-running counter, which the BE stops at Invited.
  activeTimeOnCreationHours: 'Active Time on Creation (Hours)',
  activeTimeOnCreationSeconds: 'Active Time on Creation (Seconds)',
  completionStage: 'Completion Stage',
  documentType: 'Document Type',
  engagementStage: 'Engagement Stage',
  enrollmentType: 'Enrollment Type',
  lastLogin: 'Last Login',
  matchCount: 'Match Count',
  matchCreator: 'Match Creator',
  matchFeature: 'Match Feature',
  mcqScore: 'MCQ Score',
  objectiveScore: 'Objective Score',
  onboardingStatus: 'Onboarding Status',
  onboardedDate: 'Onboarded Date',
  organizationName: 'Organization Name',
  organizationType: 'Organization Type',
  progressScore: 'Progress Score',
  programCreationFlow: 'Program Creation Flow',
  programModel: 'Program Model',
  programName: 'Program Name',
  programStatus: 'Program Status',
  resourceType: 'Resource Type',
  userEmail: 'User Email',
  userName: 'User Name',
  userProgramStatus: 'User Program Status',
  userRole: 'User Role',
  meetingType: 'Meeting Type'
} as const;

export type AttributeKeyId = keyof typeof attributeKeys;
export type AttributeDisplayName = (typeof attributeKeys)[AttributeKeyId];

/** Allowed attribute values (use these strings for consistent reporting). */
export const attributeValues = {
  organizationType: ['Corporate', 'Higher Ed', 'Association'] as const,
  programModel: ['Masterclass', 'Guided Masterclass', 'Mentorship'] as const,
  userRole: ['Mentee', 'Mentor', 'Admin'] as const,
  adminProgramStatus: ['In Design', 'Published', 'Invited', 'Active'] as const,
  userProgramStatus: ['Not Started', 'In Progress', 'Completed'] as const,
  documentType: ['File', 'Text', 'URL'] as const,
  matchCreator: ['Admin Created', 'Mentor Created'] as const,
  matchFeature: ['Auto', 'Manual'] as const,
  programCreationFlow: ['AI MD', 'Manual', 'Template'] as const,
  enrollmentType: ['Manual Invite', 'Auto Enrollment'] as const
} as const;

export type OrganizationType = (typeof attributeValues.organizationType)[number];
export type ProgramModel = (typeof attributeValues.programModel)[number];
export type UserRole = (typeof attributeValues.userRole)[number];
export type AdminProgramStatus = (typeof attributeValues.adminProgramStatus)[number];
export type UserProgramStatus = (typeof attributeValues.userProgramStatus)[number];
export type DocumentType = (typeof attributeValues.documentType)[number];
export type MatchCreator = (typeof attributeValues.matchCreator)[number];
export type MatchFeature = (typeof attributeValues.matchFeature)[number];
export type ProgramCreationFlow = (typeof attributeValues.programCreationFlow)[number];
export type EnrollmentType = (typeof attributeValues.enrollmentType)[number];

/**
 * Typed bag for standard product properties (values sent to Amplitude).
 * Keys are the same display strings as `attributeKeys` values.
 */
export type AmplitudeStandardProperties = Partial<{
  [K in AttributeDisplayName]:
    | string
    | number
    | boolean
    | null
    | undefined
    | OrganizationType
    | ProgramModel
    | UserRole
    | AdminProgramStatus
    | UserProgramStatus
    | DocumentType
    | MatchCreator
    | MatchFeature
    | ProgramCreationFlow
    | EnrollmentType;
}>;

/** Map internal key ids (e.g. programName) to Amplitude property names + values. */
export type TrackingPropsById = Partial<{
  [K in AttributeKeyId]: K extends 'progressScore' | 'objectiveScore' | 'matchCount' | 'mcqScore'
    ? number | string | null | undefined
    : K extends 'organizationType'
      ? OrganizationType | string
      : K extends 'programModel'
        ? ProgramModel | string
        : K extends 'userRole'
          ? UserRole | string
          : K extends 'adminProgramStatus'
            ? AdminProgramStatus | string
            : K extends 'userProgramStatus'
              ? UserProgramStatus | string
              : K extends 'documentType'
                ? DocumentType | string
                : K extends 'matchCreator'
                  ? MatchCreator | string
                  : K extends 'matchFeature'
                    ? MatchFeature | string
                    : K extends 'programCreationFlow'
                      ? ProgramCreationFlow | string
                      : K extends 'enrollmentType'
                        ? EnrollmentType | string
                        : string | number | boolean | null | undefined;
}>;

/**
 * Converts short property ids (`attributeKeys` keys) into Amplitude payload keys.
 * Skips undefined / null / '' so you don’t pollute events.
 */
export function buildTrackingPropsFromIds(props: TrackingPropsById): AmplitudeStandardProperties {
  const out: AmplitudeStandardProperties = {};
  (Object.keys(props) as AttributeKeyId[]).forEach((id) => {
    const v = props[id];
    if (v === undefined || v === null || v === '') return;
    const key = attributeKeys[id] as keyof AmplitudeStandardProperties;
    (out as Record<string, unknown>)[key as string] = v;
  });
  return out;
}

/**
 * Merge multiple property objects (later keys win). Useful for base context + event-specific props.
 */
export function mergeTrackingProps(
  ...parts: Array<AmplitudeStandardProperties | Record<string, unknown>>
): Record<string, unknown> {
  return Object.assign({}, ...parts.filter(Boolean));
}

export const eventTracking = (eventName: string, eventProperties: Record<string, any> = {}) => {
  // The automated QA harness (QA Admin & co.) drives real flows on every run;
  // its events would show up in every Amplitude report as product usage.
  if (isQaHarnessSession()) return;
  initAmplitude();
  amplitude.track(eventName, eventProperties);
};

/**
 * Track a taxonomy event with optional standard + extra properties.
 */
export function trackProductEvent(
  eventName: AmplitudeEventName,
  props: AmplitudeStandardProperties & Record<string, unknown> = {}
): void {
  eventTracking(eventName, props as Record<string, unknown>);
}
